import React from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { AddressType } from '@sweetgreen/garnish';

import { useCart } from '@order/hooks';

import { useBagRestaurantIds } from '../../components/Bag/Bag.hooks';
import type { DeliveryOrderDetail } from '../../graphql/types';
import { useNavigateToMenu } from '../../hooks/useNavigateToMenu';
import { useNavigateToMainTabs } from '../../navigation/AppNavigation.hooks';
import type { AppStackParamList } from '../../navigation/AppNavigation.props';
import { useConflictReview } from '../ReorderingScreen/hooks';

export const useHomeNavigation = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const navigateToMainTabs = useNavigateToMainTabs();

  const handleGoHome = React.useCallback(() => {
    void navigateToMainTabs('HomeTab', { screen: 'Home' });
  }, [navigateToMainTabs]);

  const handleGoToLocations = React.useCallback(
    (locationSelectionType: AddressType) => {
      navigation.navigate('MainTabs', {
        screen: 'MenuTab',
        params: {
          screen: 'Location',
          params: { locationSelectionType },
        },
      });
    },
    [navigation],
  );

  const handleGoToDefaultLocations = React.useCallback(() => {
    navigation.navigate('MainTabs', {
      screen: 'MenuTab',
      params: {
        screen: 'Location',
        params: {
          locationSelectionType: 'pickup',
        },
      },
    });
  }, [navigation]);

  return {
    handleGoHome,
    handleGoToLocations,
    handleGoToDefaultLocations,
  };
};

// Selecting a featured channel to go to a menu should reorder if the bag menu is different.
// If there is an in-flight delivery order, should not reorder to delivery.
export const useFeaturedChannelNavigation = () => {
  const navigateToMenu = useNavigateToMenu();
  const conflictReview = useConflictReview();
  const { cart } = useCart();
  const { bagOrderType, bagRestaurantSlug, bagAddressId } =
    useBagRestaurantIds();

  return React.useCallback(
    (payload: {
      readonly orderType: AddressType;
      readonly restaurantId?: string;
      readonly restaurantSlug?: string;
      readonly restaurantName?: string;
      readonly deliveryOrderDetail?: Partial<DeliveryOrderDetail>;
    }) => {
      const isSameOrderType = payload.orderType === bagOrderType;
      const isSameRestaurantSlug =
        Boolean(bagRestaurantSlug) &&
        payload.restaurantSlug === bagRestaurantSlug;
      const isSameAddressId =
        Boolean(bagAddressId) &&
        payload.deliveryOrderDetail?.address?.id === bagAddressId;
      const hasBag = Boolean(bagRestaurantSlug) || Boolean(bagAddressId);
      const isBagDifferent =
        !isSameOrderType || !(isSameRestaurantSlug || isSameAddressId);
      const needsToReorder = hasBag && isBagDifferent;

      // default behavior without reorder is to open the menu.
      if (!needsToReorder) {
        navigateToMenu(payload);

        return;
      }

      // if your bag belongs to a different restaurant, start reorder flow
      void conflictReview({
        orderId: cart?.id,
        orderType: payload.orderType,
        deliveryAddressName: payload.deliveryOrderDetail?.address?.name ?? '',
        deliveryOrderDetail: payload.deliveryOrderDetail?.address?.id
          ? {
              addressId: payload.deliveryOrderDetail?.address?.id,
              ...payload.deliveryOrderDetail,
            }
          : undefined,
        restaurantId: payload.restaurantId,
        restaurantSlug: payload.restaurantSlug,
        restaurantName: payload.restaurantName,
        finishOnMenu: true,
      });
    },
    [
      cart?.id,
      bagAddressId,
      bagOrderType,
      bagRestaurantSlug,
      conflictReview,
      navigateToMenu,
    ],
  );
};
